// src/pages/chat/welcome-content/welcome-content.tsx
/** @jsxImportSource @emotion/react */
import React, { useMemo, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import { WelcomeContainer, TitleSlot, WelcomeTitle, LogoWrapper, StatusText } from './welcome-content.styled';
import { useAuth } from '../../../contexts/auth/auth-context';
import CTA, { type CtaHandle, type Phase } from '../CTA/cta';
import { ENVIRONMENT } from '../../../env';

const DEV = ENVIRONMENT === 'DEV';
const log = (...args: any[]) => {
  if (DEV) console.log('[welcome-content.tsx]', ...args);
};

const pauseOff = '/cta/pause-off.mp4';
const pauseOn = '/cta/pause-on.mp4';
const recordingLoop = '/cta/recording-loop.mp4';
const restToRecording = '/cta/rest-to-recording.mp4';
const restLoop = '/cta/rest-loop.mp4';
const stopRecordingFromPause = '/cta/stop-recording-from-pause.mp4';
const stopRecordingFromRecording = '/cta/stop-recording-from-recording.mp4';

function titleCase(s: string) {
  return s
    .trim()
    .split(/\s+/g)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function getTimeSensitiveGreeting(now: Date, name: string) {
  const hour = now.getHours(); // client/local time
  const safeName = name.trim();

  // HARD fallback (should rarely happen) — still no comma/name weirdness
  if (!safeName) return 'Welcome back.';

  // 5:00 - 8:59
  if (hour >= 5 && hour <= 8) return `Good morning, ${safeName}!`;

  // 9:00 - 17:59
  if (hour >= 9 && hour <= 17) return `Hi, ${safeName}!`;

  // 18:00 - 19:59
  if (hour >= 18 && hour <= 19) return `Good evening, ${safeName}!`;

  // 20:00 - 4:59
  return `Welcome back, ${safeName}!`;
}

/**
 * Title always stays in the DOM — opacity is driven by `isFullScreenRecordingMode`.
 * No mount/unmount, no AnimatePresence, so there is zero layout shift.
 * Transition: 0.35s ease-out.
 */
export const TITLE_FADE_DURATION = 0.35;

export const statusFade = {
  initial: { opacity: 0 },
  animate: { opacity: 1, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] as const } },
  exit: { opacity: 0, transition: { duration: 0.3, ease: [0.4, 0, 1, 1] as const } },
};

export function getStatusText(phase: Phase, isStillProcessing: boolean): string | null {
  switch (phase) {
    case 'recording':
    case 'toRecording':
    case 'toResume':
      return "I'm listening\u2026";
    case 'paused':
    case 'toPaused':
      return 'Paused\u2014resume when ready.';
    case 'toStop':
      return isStillProcessing ? 'Still processing.' : 'Preparing your draft\u2026';
    default:
      return null;
  }
}

export type WelcomeContentProps = {
  isFullScreenRecordingMode: boolean;
  setIsFullScreenRecordingMode: React.Dispatch<React.SetStateAction<boolean>>;
  ctaRef?: React.RefObject<CtaHandle | null>;
  onCtaPhaseChange?: (p: Phase) => void;

  // ✅ NEW: CTA should send audio + transcript on stop
  onSendRecording?: (audioBlob: Blob, transcript?: string) => void;

  // ✅ Error callback when recording fails (e.g. no audio captured)
  onRecordingFailed?: (message: string) => void;

  // ✅ Disable CTA interaction (e.g. dock recording in progress, submission in-flight)
  disabled?: boolean;
};

export const WelcomeContent: React.FC<WelcomeContentProps> = ({
  isFullScreenRecordingMode,
  setIsFullScreenRecordingMode,
  ctaRef,
  onCtaPhaseChange,
  onSendRecording,
  onRecordingFailed,
  disabled = false,
}) => {
  const { profile, user, loading } = useAuth();

  const [currentPhase, setCurrentPhase] = useState<Phase>('restLoop');
  const [isStillProcessing, setIsStillProcessing] = useState(false);

  useEffect(() => {
    log('isFullScreenRecordingMode =', isFullScreenRecordingMode);
  }, [isFullScreenRecordingMode]);

  // "Still processing" timer: after 4s in toStop phase
  useEffect(() => {
    if (currentPhase !== 'toStop') {
      setIsStillProcessing(false);
      return;
    }

    const timer = setTimeout(() => {
      setIsStillProcessing(true);
    }, 4000);

    return () => clearTimeout(timer);
  }, [currentPhase]);

  const displayName = useMemo(() => {
    const nick = (profile?.nickname ?? '').trim();
    if (nick) return titleCase(nick);

    const first = (profile?.first_name ?? (profile as any)?.firstName ?? '').trim();
    if (first) return titleCase(first);

    const email = (user?.email ?? (profile as any)?.email_address ?? '').trim();
    if (email && email.includes('@'))
      return titleCase(email.split('@')[0]?.replace(/[._-]+/g, ' '));

    return '';
  }, [profile, user]);

  const titleText = useMemo(() => {
    return getTimeSensitiveGreeting(new Date(), displayName);
  }, [displayName]);

  const statusText = getStatusText(currentPhase, isStillProcessing);

  return (
    <WelcomeContainer>
      <TitleSlot>
        {/* Fade opacity only — no layout shift so the title doesn't slide behind the M */}
        {!loading && (
          <motion.div
            animate={{ opacity: isFullScreenRecordingMode ? 0 : 1 }}
            transition={{ duration: TITLE_FADE_DURATION, ease: [0.4, 0, 1, 1] }}
          >
            <WelcomeTitle>{titleText}</WelcomeTitle>
          </motion.div>
        )}
      </TitleSlot>

      <LogoWrapper aria-label={isFullScreenRecordingMode ? 'Stop recording' : 'Start recording'}>
        <CTA
          ref={ctaRef as any}
          disabled={disabled}
          videos={{
            pauseOff,
            pauseOn,
            recordingLoop,
            restToRecording,
            restLoop,
            stopRecordingFromPause,
            stopRecordingFromRecording,
          }}
          defaultRecording={false}
          onPhaseChange={(p) => {
            log('CTA phase =', p);
            setCurrentPhase(p);
            onCtaPhaseChange?.(p);
          }}
          onStartRecording={() => {
            log('CTA onStartRecording -> fullscreen ON');
            setIsFullScreenRecordingMode(true);
          }}
          onStopRecording={() => {
            log('CTA onStopRecording (requested)');
          }}
          onReturnToRest={() => {
            log('CTA onReturnToRest -> fullscreen OFF');
            setIsFullScreenRecordingMode(false);
          }}
          onSendRecording={onSendRecording}
          onRecordingFailed={onRecordingFailed}
        />
        <AnimatePresence mode="wait">
          {statusText && (
            <motion.div key={statusText} {...statusFade}>
              <StatusText>{statusText}</StatusText>
            </motion.div>
          )}
        </AnimatePresence>
      </LogoWrapper>
    </WelcomeContainer>
  );
};
